import { FilePdfOutlined } from '@ant-design/icons';
import { Alert, Button, Result, Skeleton, Tag } from 'antd';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useQuery } from 'urql';
import { cubicMeters, dirhams, fullDate, monthYear } from '../format';
import { graphql } from '../gql';
import { IdTag } from '../IdTag';
import { toCents } from '../money';
import { PhotoView } from '../photos/PhotoField';
import { appliedFees, readBreakdown, tierRange, usedTiers } from './breakdown';
import './bill-details.css';

const BillDetailsDocument = graphql(`
  query BillDetails($id: UUID!) {
    bill(id: $id) {
      id
      billMonth
      dueDate
      billStatus
      previousReading
      currentReading
      consumption
      billTotal
      paidAmount
      remainingAmount
      breakdown
      invoiceLink
      receiptLink
      readingPhoto {
        url
        uploadedBy
        createdAt
      }
      member {
        id
        fullName
      }
      meter {
        id
        label
      }
      billingConfiguration {
        name
      }
      payments(orderBy: [PAYMENT_DATE_ASC]) {
        nodes {
          id
          amount
          paymentDate
          receiptNo
          receiptLink
        }
      }
    }
  }
`);

/** A bill's status as arqam computes it, in Arabic, with its tag colour. */
export const BILL_STATUS = {
  PAID: { label: 'مؤداة', color: 'green' },
  PENDING: { label: 'غير مؤداة', color: 'orange' },
  OVERDUE: { label: 'متأخرة', color: 'red' },
} as const;

/**
 * One bill, as arqam computed it: what was billed and why (tiers, fees,
 * shart), what was paid and what remains, its receipts and documents. Shared
 * by the payments and committee apps; each gives its own links and actions.
 */
export function BillDetails({
  billId,
  memberLink,
  meterLink,
  actions,
}: {
  billId: string;
  /** Where the member's name leads in this app. */
  memberLink: (memberId: string) => string;
  /** Where the meter leads, if this app has meter pages. */
  meterLink?: (meterId: string) => string;
  /** This app's actions for the bill (e.g. pay it). */
  actions?: (bill: {
    id: string;
    memberId: string;
    paid: boolean;
  }) => ReactNode;
}) {
  const [{ data, error }] = useQuery({
    query: BillDetailsDocument,
    variables: { id: billId },
  });

  if (!data) {
    return error ? (
      <Alert type="error" showIcon message="تعذّر تحميل الفاتورة" />
    ) : (
      <Skeleton active paragraph={{ rows: 10 }} />
    );
  }
  const bill = data.bill;
  if (!bill) return <Result status="404" title="الفاتورة غير موجودة" />;

  const status = BILL_STATUS[bill.billStatus];
  const paidCents = toCents(bill.paidAmount ?? '0');
  const breakdown = readBreakdown(bill.breakdown);
  const payments = bill.payments.nodes.flatMap((p) => (p ? [p] : []));
  // Two old bills have a stored total that differs from their breakdown
  // (arqam doc 34): show it rather than hide it.
  const breakdownDiffers =
    breakdown !== null &&
    Math.round(breakdown.totalBill * 100) !== toCents(bill.billTotal);

  return (
    <div className="bill-details">
      <section className="card">
        <div className="bill-title-row">
          <h1 className="bill-title">فاتورة {monthYear(bill.billMonth)}</h1>
          <Tag color={status.color} className="bill-status">
            {status.label}
          </Tag>
        </div>
        <dl className="bill-facts">
          {bill.member && (
            <>
              <dt>العضو</dt>
              <dd>
                <Link to={memberLink(bill.member.id)}>
                  {bill.member.fullName}
                </Link>
              </dd>
            </>
          )}
          {bill.meter && (
            <>
              <dt>العداد</dt>
              <dd>
                {meterLink ? (
                  <Link to={meterLink(bill.meter.id)}>{bill.meter.label}</Link>
                ) : (
                  bill.meter.label
                )}
              </dd>
            </>
          )}
          {bill.dueDate && (
            <>
              <dt>آخر أجل للأداء</dt>
              <dd>{fullDate(bill.dueDate)}</dd>
            </>
          )}
        </dl>
      </section>

      <section className="card">
        <dl className="bill-amounts">
          <dt>مبلغ الفاتورة</dt>
          <dd>{dirhams(bill.billTotal)}</dd>
          {paidCents > 0 && (
            <>
              <dt>المؤدّى</dt>
              <dd>{dirhams(bill.paidAmount)}</dd>
            </>
          )}
          <dt>الباقي</dt>
          <dd className={bill.billStatus === 'PAID' ? 'is-paid' : 'is-due'}>
            {bill.billStatus === 'PAID'
              ? 'لا شيء'
              : dirhams(bill.remainingAmount)}
          </dd>
        </dl>
        {actions?.({
          id: bill.id,
          memberId: bill.member?.id ?? '',
          paid: bill.billStatus === 'PAID',
        })}
      </section>

      <section className="card">
        <div className="field-label">الاستهلاك</div>
        <div className="reading-line">
          <span className="ltr-num">
            {bill.previousReading} → {bill.currentReading}
          </span>
          <strong>{cubicMeters(bill.consumption)}</strong>
        </div>
      </section>

      {bill.readingPhoto && (
        <section className="card">
          <div className="field-label">صورة العداد عند القراءة</div>
          <PhotoView
            url={bill.readingPhoto.url}
            alt={`صورة العداد لفاتورة ${monthYear(bill.billMonth)}`}
            caption={`${fullDate(bill.readingPhoto.createdAt)}${bill.readingPhoto.uploadedBy ? ` · ${bill.readingPhoto.uploadedBy}` : ''}`}
          />
        </section>
      )}

      <section className="card">
        <div className="field-label">تفصيل المبلغ</div>
        {breakdown ? (
          <ul className="breakdown">
            {usedTiers(breakdown).map((t) => (
              <li key={t.tierStart}>
                <span>
                  {t.description ?? tierRange(t)}
                  <span className="field-hint">
                    {' '}
                    ({tierRange(t)}): {cubicMeters(t.consumptionInTier)} ×{' '}
                    {dirhams(t.rate)}
                  </span>
                </span>
                <span className="amount">{dirhams(t.tierPrice)}</span>
              </li>
            ))}
            {usedTiers(breakdown).length === 0 && (
              <li>
                <span>الاستهلاك</span>
                <span className="amount">
                  {dirhams(breakdown.consumptionPrice)}
                </span>
              </li>
            )}
            {appliedFees(breakdown).map((f) => (
              <li key={f.name}>
                <span>
                  {f.name}
                  {f.feeType === 'PERCENTAGE' && (
                    <span className="field-hint"> ({f.amount}%)</span>
                  )}
                </span>
                <span className="amount">{dirhams(f.calculatedAmount)}</span>
              </li>
            ))}
            {breakdown.shart.totalPrice > 0 && (
              <li>
                <span>
                  الشرط
                  <span className="field-hint">
                    {' '}
                    ({breakdown.shart.memberCount} ×{' '}
                    {dirhams(breakdown.shart.unitPrice)}
                    {breakdown.shart.members?.length
                      ? `: ${breakdown.shart.members.join('، ')}`
                      : ''}
                    )
                  </span>
                </span>
                <span className="amount">
                  {dirhams(breakdown.shart.totalPrice)}
                </span>
              </li>
            )}
            <li className="breakdown-total">
              <span>المجموع</span>
              <span className="amount">{dirhams(breakdown.totalBill)}</span>
            </li>
          </ul>
        ) : (
          <div className="field-hint">لا يتوفر تفصيل لهذه الفاتورة.</div>
        )}
        {breakdownDiffers && (
          <Alert
            type="warning"
            showIcon
            message={`المبلغ المسجّل في الفاتورة (${dirhams(bill.billTotal)}) يختلف عن هذا التفصيل. يُعتمد المبلغ المسجّل.`}
          />
        )}
        {bill.billingConfiguration && (
          <div className="field-hint">
            حسب أسعار «{bill.billingConfiguration.name}».
          </div>
        )}
      </section>

      <section className="card">
        <div className="field-label">الأداءات</div>
        {payments.length === 0 ? (
          <div className="field-hint">لم يُؤدَّ شيء بعد.</div>
        ) : (
          <ul className="plain-list">
            {payments.map((p) => (
              <li key={p.id} className="payment-line">
                <span>
                  <strong>{dirhams(p.amount)}</strong>
                  <div className="field-hint">
                    {fullDate(p.paymentDate)} · وصل{' '}
                    <span className="ltr-num">{p.receiptNo}</span>
                  </div>
                  <IdTag id={p.id} label="معرّف الأداء" />
                </span>
                <Button href={p.receiptLink} target="_blank">
                  الوصل
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card">
        <div className="field-label">الوثائق</div>
        <div className="bill-documents">
          <Button
            href={bill.invoiceLink}
            target="_blank"
            size="large"
            icon={<FilePdfOutlined />}
          >
            الفاتورة (PDF)
          </Button>
          <Button
            href={bill.receiptLink}
            target="_blank"
            size="large"
            icon={<FilePdfOutlined />}
          >
            وصل الأداء (PDF)
          </Button>
        </div>
      </section>

      <IdTag id={bill.id} label="معرّف الفاتورة" />
    </div>
  );
}
